import IframeResizer from "iframe-resizer-react";
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import ModalLinearActivityIndicator from "./components/ModalLinearActivityIndicator";
import { get as _get } from "lodash";
import { Theme, useMediaQuery, useTheme } from "@mui/material";
import GenericModal from "./components/GenericModal";
import { DatadogResolver } from "./Utils/DatadogResolver";
import { datadogRum } from "@datadog/browser-rum";
import {
  GenericModalTopMargin,
  GlassClaimConfirmationConstants,
} from "./Constants/Constants";

// Extend the Window interface to include webkit and Android
declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        postMessageHandler?: {
          postMessage: (postData: any) => void;
        };
      };
    };
    Android?: {
      postMessageHandler: (message: string) => void;
    };
  }
}

enum POST_MESSAGE_CODE {
  APP_ACTIVE = "DIGITAL_CLAIM_V2_APP_IS_ACTIVE",
  SCROLL = "DIGITAL_CLAIM_V2_SCROLL",
  LAUNCH_SAFELITE = "DIGITAL_CLAIM_V2_LAUNCH_SAFELITE",
  RELOAD = "DIGITAL_CLAIM_V2_RELOAD_BUTTON_CLICK",
  HOSTED_BY_BRIDGE_APP = "HOSTED_BY_BRIDGE_APP",
}

const DCV2_UI_ERROR_CODES = [
  "DIGITAL_CLAIM_V2_SESSION_TIMEOUT_ERROR",
  "DIGITAL_CLAIM_V2_USER_SESSION_VALIDATE_FAIL_ERROR",
  "DIGITAL_CLAIM_V2_CLAIM_DETAIL_FAIL_ERROR",
  "DIGITAL_CLAIM_V2_POLICY_RETRIEVAL_ERROR",
  "DIGITAL_CLAIM_V2_SAFE_LITE_PAYLOAD_FAIL_ERROR",
  "DIGITAL_CLAIM_V2_DRAFT_CLAIM_CREATE_ERROR",
  "DIGITAL_CLAIM_V2_DRAFT_CLAIM_DELETE_ERROR",
  "DIGITAL_CLAIM_V2_CONVERSE_NEXT_ERROR",
  "DIGITAL_CLAIM_V2_CONVERSE_BACK_ERROR",
  "DIGITAL_CLAIM_V2_CONVERSE_EDIT_ERROR",
  "DIGITAL_CLAIM_V2_CONVERSE_SUBMIT_ERROR",
  "DIGITAL_CLAIM_V2_ALL_CLAIMS_RETRIEVAL_ERROR",
  "DIGITAL_CLAIM_V2_AUTO_ELIGIBLE_SERVICES_ERROR",
  "DIGITAL_CLAIM_V2_AUTO_CLAIM_SUBMIT_TIMEOUT_ERROR",
  "DIGITAL_CLAIM_V2_AUTO_DRP_SHOPS_RETRIEVAL_FAIL_ERROR",
  "DIGITAL_CLAIM_V2_AUTO_DRP_SHOP_SELECTION_FAIL_ERROR",
];

interface LogDdRumErrorParams {
  code: string;
  error?: unknown;
}

const isMyAccountMobileUserAgent = () => {
  const userAgent = navigator.userAgent;

  if (
    userAgent.includes("MyAccount_iOS") ||
    userAgent.includes("MyAccount_Android")
  ) {
    //console.log("User agent matches MyAccount_iOS or MyAccount_Android");
    return true;
  } else {
    //console.log("User agent does not match any custom names");
    return false;
  }
};

const RedirectPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const theme: Theme = useTheme();
  const [initialLoading, setInitialLoading] = useState(true);
  const [glassClaimVendorModalOpen, setGlassClaimVendorModalOpen] =
    useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [glassClaimVendorHtml, setGlassClaimVendorHtml] = useState("");
  const destinationUrl = `${process.env["REACT_APP_CONTEXT_ROUTER_URL"]}/${id}`;
  const ClaimsIframe = "ClaimsV2Iframe";
  // Memoize trusted origins to prevent unnecessary re-renders
  const trustedOrigins = useMemo(
    () => [process.env.REACT_APP_DCV2_UI_APP_URL].filter(Boolean) as string[],
    [],
  );

  const onResized = (data: any) => {
    //console.log("DCV2-UI is resized:", data);
  };
  const hideSpinner = () => setInitialLoading(false);
  const showSpinner = () => setInitialLoading(true);

  // Relaying SCROLL up to MyAccount immediately races the bridge app's own
  // height report to MyAccount (sent by the iframe-resizer contentWindow
  // script watching [data-iframe-height], triggered by DCV2-UI's own resize
  // flowing into this app's <IframeResizer>). If MyAccount scrolls before it
  // has applied our latest height, content ends up cut off at the top.
  // Mirrors DCV2-UI's ScrollMessageWrapperHeightStabilized: wait for
  // [data-iframe-height] to stop changing size before relaying the scroll.
  const scrollSettleTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollForceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollResizeObserverRef = React.useRef<ResizeObserver | null>(null);

  const relayScrollWhenSettled = React.useCallback(() => {
    const SETTLE_THRESHOLD = 300; // ms - wait after last height change before relaying
    const MAX_WAIT_TIMEOUT = 2000; // ms - safety fallback if height never settles

    const cleanup = () => {
      if (scrollSettleTimerRef.current) {
        clearTimeout(scrollSettleTimerRef.current);
        scrollSettleTimerRef.current = null;
      }
      if (scrollForceTimerRef.current) {
        clearTimeout(scrollForceTimerRef.current);
        scrollForceTimerRef.current = null;
      }
      if (scrollResizeObserverRef.current) {
        scrollResizeObserverRef.current.disconnect();
        scrollResizeObserverRef.current = null;
      }
    };

    // Cancel any relay still pending from a prior SCROLL message before starting a new wait
    cleanup();

    let sent = false;
    const sendScroll = () => {
      if (sent) return;
      sent = true;
      cleanup();
      postMessageToParent({ messageName: POST_MESSAGE_CODE.SCROLL });
    };

    scrollForceTimerRef.current = setTimeout(sendScroll, MAX_WAIT_TIMEOUT);

    const heightEl = document.querySelector("[data-iframe-height]");
    if (heightEl) {
      scrollResizeObserverRef.current = new ResizeObserver(() => {
        if (scrollSettleTimerRef.current) {
          clearTimeout(scrollSettleTimerRef.current);
        }
        scrollSettleTimerRef.current = setTimeout(sendScroll, SETTLE_THRESHOLD);
      });
      scrollResizeObserverRef.current.observe(heightEl);
    } else {
      // No tagged element found to watch; relay immediately rather than block forever
      sendScroll();
    }
  }, []);

  React.useEffect(() => {
    return () => {
      if (scrollSettleTimerRef.current) clearTimeout(scrollSettleTimerRef.current);
      if (scrollForceTimerRef.current) clearTimeout(scrollForceTimerRef.current);
      if (scrollResizeObserverRef.current) scrollResizeObserverRef.current.disconnect();
    };
  }, []);

  React.useEffect(() => {
    const environment = process.env["REACT_APP_BRIDGE_APP_ENVIRONMENT"] || "";
    const version =
      process.env["REACT_APP_DIGITAL_CLAIMS_BRIDGE_APP_VERSION"] || "";
    const build =
      process.env["REACT_APP_DIGITAL_CLAIMS_BRIDGE_APP_BUILD_NUMBER"] || "";
    console.log(
      `Digital Claims Bridge App -  Environment: ${environment} Version#: ${version} Build#: ${build}`,
    );
    datadogRum.init(DatadogResolver.resolveConfig(environment, version));
  }, []);

  function sendMessageToChildApp() {
    const iframeElement = document.getElementById(
      ClaimsIframe,
    ) as HTMLIFrameElement | null;
    if (
      iframeElement &&
      iframeElement instanceof HTMLIFrameElement &&
      iframeElement.contentWindow
    ) {
      iframeElement.contentWindow.postMessage(
        { messageName: POST_MESSAGE_CODE.HOSTED_BY_BRIDGE_APP },
        "*",
      );
    } else {
      console.error("ClaimsV2Iframe not found or is not an iframe.");
    }
  }

  function logDdRumError({ code, error }: LogDdRumErrorParams) {
    const ddRum = (window as any)?.DD_RUM;
    if (ddRum?.addError && typeof ddRum.addError === "function") {
      const err = error instanceof Error ? error : new Error(code);
      const attributes = {
        messageName: code,
        source: "digital-claims-bridge-spa",
        timestamp: new Date().toISOString(),
      };
      ddRum.addError(err, attributes);
    }
  }

  React.useEffect(() => {
    const messageHandler = async (event: MessageEvent) => {
      if (trustedOrigins.includes(event.origin)) {
        const code = _get(event, "data.messageName", "NO_MESSAGE_FOUND");
        switch (code) {
          case POST_MESSAGE_CODE.APP_ACTIVE:
            hideSpinner();
            sendMessageToChildApp();
            break;
          case POST_MESSAGE_CODE.SCROLL:
            relayScrollWhenSettled();
            if (isMyAccountMobileUserAgent()) {
              // Scrolling this window to its own top is always safe regardless
              // of in-flight height changes, so this doesn't need to wait.
              window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
            }
            break;
          case POST_MESSAGE_CODE.LAUNCH_SAFELITE:
            receiveGlassVendorHTMLContent(event.data.htmlContent);
            break;
          case POST_MESSAGE_CODE.RELOAD:
            showSpinner();
            postMessageToParent({ messageName: POST_MESSAGE_CODE.RELOAD });
            console.log("Bridge-app: Reload message sent to parent app");
            break;
          default:
            // Error handling for all listed error codes
            if (DCV2_UI_ERROR_CODES.includes(code)) {
              logDdRumError({
                code,
                error: event?.data?.error,
              });
            }
            break;
        }
      }
    };
    window.addEventListener("message", messageHandler, false);
    return () => {
      window.removeEventListener("message", messageHandler);
    };
  }, [trustedOrigins, relayScrollWhenSettled]);

  const receiveGlassVendorHTMLContent = (htmlData: any) => {
    setGlassClaimVendorModalOpen(true);
    setGlassClaimVendorHtml(htmlData);
  };

  const handleGoToSafelite = () => {
    if (isMyAccountMobileUserAgent()) {
      // If running in a WKWebView, send postMessage with the data
      const postData = {
        messageName: POST_MESSAGE_CODE.LAUNCH_SAFELITE,
        data: glassClaimVendorHtml,
      };
      postMessageToParent(postData);
    } else {
      // If running in a desktop browser or iOS Safari - 1. Send reload message to parent 2. Execute the Blob logic
      // 1. Send reload message to parent
      const postData = {
        messageName: POST_MESSAGE_CODE.RELOAD,
      };
      postMessageToParent(postData);
      // 2. Execute the Blob logic
      const blob = new Blob([glassClaimVendorHtml], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
    console.log("Bridge-app: Glass-claim-redirect message sent to parent app");
    setGlassClaimVendorModalOpen(false);
  };

  function postMessageToParent(message: { messageName: string; data?: any }) {
    // Create the postData object, including data only if it exists in the input
    const postData: { messageName: string; data?: any } = {
      messageName: message.messageName,
    };

    if (message.data !== undefined) {
      postData.data = message.data;
    }

    // Use webkit message handler if available (for iOS)
    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.postMessageHandler
    ) {
      window.webkit.messageHandlers.postMessageHandler.postMessage(postData);
    }
    // Use Android interface if available
    else if (window.Android && window.Android.postMessageHandler) {
      window.Android.postMessageHandler(JSON.stringify(postData));
    } else {
      // Sending post messages to other scenario like a web browser
      window.parent.postMessage(postData, "*");
      console.log(
        `Bridge-app: Post message ${JSON.stringify(
          postData,
        )} sent to parent app`,
      );
    }
  }

  const glassClaimVendorModal = (
    <GenericModal
      title={GlassClaimConfirmationConstants.TITLE}
      description={GlassClaimConfirmationConstants.DESCRIPTION}
      primaryButtonText={GlassClaimConfirmationConstants.PRIMARY_BUTTON_TEXT}
      alertBoxStyle={theme.custom.styles.genericModalAlertBoxStyle}
      verticalPositionForMobile={GenericModalTopMargin}
      theme={theme}
      closeButtonActionHandler={handleGoToSafelite}
      primaryButtonActionHandler={handleGoToSafelite}
    ></GenericModal>
  );

  return (
    <>
      {initialLoading && <ModalLinearActivityIndicator />}
      {!!glassClaimVendorModalOpen && glassClaimVendorModal}
      <IframeResizer
        src={destinationUrl}
        id={ClaimsIframe}
        style={{
          width: "1px",
          minWidth: "100%",
          minHeight: isMobile ? "667px" : "900px",
          borderWidth: "0px",
          height: "auto",
        }}
        onResized={onResized}
        heightCalculationMethod="taggedElement"
        scrolling={false}
        inPageLinks={false}
        // Sub-pixel height jitter under fractional devicePixelRatio (e.g. Windows
        // 150% scale x 67% browser zoom = DPR ~1.005) otherwise triggers an endless
        // resize loop. A 2px tolerance lets the height settle. (E1PCDC-6412)
        tolerance={2}
        checkOrigin={trustedOrigins}
      />
    </>
  );
};

export default RedirectPage;
